import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { useSeasonYears } from '../hooks/useSeasonYears';

interface PaletteItem {
  label: string;
  to: string;
  section: string;
}

export function buildPaletteItems(currentSeasonYear: number, activeSeasonYear: number): PaletteItem[] {
  return [
    { label: 'Dashboard', to: '/dashboard', section: 'Core' },
    { label: 'Calendar', to: `/calendar/${currentSeasonYear}`, section: 'Core' },
    { label: 'Races', to: `/races/${currentSeasonYear}`, section: 'Core' },
    { label: 'Standings', to: `/standings/${activeSeasonYear}`, section: 'Core' },
    { label: 'Constructors', to: '/constructors', section: 'Explore' },
    { label: 'Circuits', to: '/circuits', section: 'Explore' },
    { label: 'Head to Head', to: '/h2h', section: 'Insights' },
    { label: 'Title Fights', to: '/title-fights', section: 'Insights' },
    { label: 'Eras', to: '/eras', section: 'Insights' },
    { label: 'Lap Times', to: '/lap-times', section: 'Insights' },
    { label: 'Records', to: '/records', section: 'Insights' },
    { label: 'Analytics', to: '/analytics', section: 'Intelligence' },
    { label: 'Predictions', to: '/predictions', section: 'Intelligence' },
    { label: 'Championship', to: '/championship', section: 'Intelligence' },
    { label: 'News', to: '/news', section: 'Live' },
  ];
}

export function CommandPalette() {
  const open = useStore((s) => s.paletteOpen);
  const setOpen = useStore((s) => s.setPaletteOpen);
  const navigate = useNavigate();
  const { currentSeasonYear, activeSeasonYear } = useSeasonYears();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const paletteItems = useMemo(
    () => buildPaletteItems(currentSeasonYear, activeSeasonYear),
    [currentSeasonYear, activeSeasonYear],
  );

  const filtered = query.trim()
    ? paletteItems.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()),
      )
    : paletteItems;

  // Cmd+K global shortcut
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(!open);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlighted(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const go = useCallback(
    (to: string) => {
      navigate(to);
      setOpen(false);
    },
    [navigate, setOpen],
  );

  function onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && filtered[highlighted]) {
      go(filtered[highlighted].to);
    }
  }

  if (!open) return null;

  return (
    <div className="palette-backdrop" onClick={() => setOpen(false)}>
      <div className="palette-shell" onClick={(e) => e.stopPropagation()}>
        <div className="palette-input-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            className="palette-input"
            placeholder="Search pages..."
            aria-label="Search commands"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlighted(0);
            }}
            onKeyDown={onInputKeyDown}
          />
        </div>
        <div className="palette-results">
          {filtered.length === 0 ? (
            <div className="palette-empty">No results found</div>
          ) : (
            filtered.map((item, i) => (
              <div
                key={item.to}
                className={`palette-item${i === highlighted ? ' highlighted' : ''}`}
                onClick={() => go(item.to)}
                onMouseEnter={() => setHighlighted(i)}
              >
                <span style={{ color: 'var(--color-text-dim)', fontSize: '11px', fontFamily: 'var(--font-data)', minWidth: '80px' }}>
                  {item.section}
                </span>
                {item.label}
              </div>
            ))
          )}
        </div>
        <div className="palette-footer">
          <span><kbd>↵</kbd> select</span>
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
